import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  assign,
  each,
  find,
  isArray,
  isNil,
  isObject,
  isUndefined,
  reduce,
  remove
} from 'lodash';
import { CardColumnDisplayBase } from 'src/app/classes/base-classes/card-column/card-column-display/card-column-display-base';
import { Card } from 'src/app/interfaces/card';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-card-column-display-modal',
  templateUrl: './card-column-display-modal.component.html',
  styleUrls: ['./card-column-display-modal.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CardColumnDisplayModalComponent
  extends CardColumnDisplayBase
  implements OnInit
{
  constructor(
    protected activeModal: NgbActiveModal,
    protected ToastService: ToastService,
    protected ChangeDetectorRef: ChangeDetectorRef,
    protected SharedDataService: SharedDataService
  ) {
    super();
  }

  @Input() showSelectAllButton: boolean = false;
  @Input() cancelButtonText = 'Cancel';
  @Input() continueButtonText = 'Continue';

  // Can display additional messages.
  @Input() AdditionalMessages: {
    style?: GenericObject;
    classes?: string;
    text: string;
  }[] = [];

  areAllCardsSelected: boolean = false;

  // Determines if every card has been selected.
  checkIfAllCardsAreSelected() {
    return (
      this.cardCollection.every((card) => card.isSelected) &&
      this.cardCollection.length > 0
    );
  }

  handleAllCardSelections() {
    this.areAllCardsSelected = !this.checkIfAllCardsAreSelected();
    this.SharedDataService.sendEvent(
      'processAllCardSelection',
      this.areAllCardsSelected
    );
  }

  ngOnInit(): void {
    each(this.cardCollection, (Card: Card) => {
      if (this.selectionModel && Card.record && isUndefined(Card.isSelected)) {
        if (isArray(this.selectionModel.data)) {
          Card.isSelected = !isNil(
            find(this.selectionModel.data, (arrayValue) =>
              Card.record.comparator(arrayValue, Card.record.value)
            )
          );
        } else if (this.selectionModel.data instanceof FormArray) {
          Card.isSelected = !isNil(
            find(this.selectionModel.data.controls, (arrayValue) =>
              Card.record.comparator(arrayValue, Card.record.value)
            )
          );
        } else {
          Card.isSelected =
            this.selectionModel.data[Card.record.recordField] ===
            Card.record.selectValue;
        }
      }
    });

    this.areAllCardsSelected = this.checkIfAllCardsAreSelected();
  }

  cancel() {
    if (this.selectionModel) {
      const { data, originalInputData } = this.selectionModel;
      if (data instanceof FormArray) {
        data.clear();
        originalInputData.controls.forEach((formGroup: FormGroup) => {
          data.push(formGroup);
        });
      } else if (Array.isArray(data)) {
        data.splice(0);
        data.push(...originalInputData);
      } else if (isObject(data)) {
        Object.assign(data, originalInputData);
      }
      return this.activeModal.close(null);
    }
    return this.activeModal.close();
  }

  continue() {
    if (this.selectionModel) {
      // If the selection model has a selectionLimit applied, then we only allow an amount of selections
      // less than or equal to the limit.
      if (this.selectionModel.selectionLimit) {
        const selectedCount: number = reduce(
          this.cardCollection,
          (accum, Card: Card) => (Card.isSelected ? accum + 1 : accum),
          0
        );
        if (selectedCount > this.selectionModel.selectionLimit) {
          throw this.ToastService.addErrorToast(
            'Error',
            'You may only select up to 12 trades.',
            { returnError: true }
          );
        }
      }
      return this.activeModal.close(this.selectionModel.data);
    }
    return this.activeModal.close();
  }

  override cardClicked(Card: Card) {
    // If a selection model was passed to this component, we can populate
    // data within the selection model.
    if (this.selectionModel) {
      // If the card contains a record (which in this case is: ArraySelectOption)
      if (Card.record) {
        if (
          isArray(this.selectionModel.data) ||
          this.selectionModel.data instanceof FormArray
        ) {
          // If the card is selected, push the value (of ArraySelectOption) of the record onto the data array.
          if (Card.isSelected) {
            // If the selection type is set to 'single', remove all elements in the underlying data array.
            if (this.selectionModel.selectionType === 'single') {
              if (isArray(this.selectionModel.data)) {
                this.selectionModel.data.splice(
                  0,
                  this.selectionModel.data.length
                );
              } else {
                this.selectionModel.data.clear();
              }
            }

            // Push the value of the ArraySelectOption onto the underlying data array.
            this.selectionModel.data.push(Card.record.value);
          } else {
            if (isArray(this.selectionModel.data)) {
              remove(this.selectionModel.data, (arrayValue) =>
                Card.record.comparator(arrayValue, Card.record.value)
              );
            } else {
              const controlIndex = this.selectionModel.data.controls.findIndex(
                (arrayValue) =>
                  Card.record.comparator(arrayValue, Card.record.value)
              );
              this.selectionModel.data.removeAt(controlIndex);
            }
          }
        } else {
          if (Card.isSelected) {
            // If the selection type is set to 'single', go through all selectable options
            // and update the underlying dataObj to the 'deselectValue' that is supplied
            // to each ObjectFieldSelectOption record.
            if (this.selectionModel.selectionType === 'single') {
              each(this.cardCollection, (Card: Card) => {
                assign(this.selectionModel!.data, {
                  [Card.record.recordField]: Card.record.deselectValue
                });
              });
            }

            // Apply the 'selectValue' that is supplied to the ObjectFieldSelectOption record
            // to the underlying dataObj.
            assign(this.selectionModel.data, {
              [Card.record.recordField]: Card.record.selectValue
            });
          } else {
            // If the card is de-selected, apply the deselect value (from ObjectFieldSelectOption)
            this.selectionModel.data[Card.record.recordField] =
              Card.record.deselectValue;
          }
        }
      }

      this.areAllCardsSelected = this.checkIfAllCardsAreSelected();
    }
  }
}
